/**
 * PortalNotificationBell — combined unread badge for announcements + portal
 * notifications, plus a click-to-open dropdown showing the latest 10 with
 * one-tap "mark read".
 *
 * Iter-412 extends the earlier announcements-only bell to also surface
 * material publish / assignment / result events from `portal_notifications`.
 */
import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const _token = () => {
  try { const p = sessionStorage.getItem('school_preview_token'); if (p) return p; }
  catch { /* ignore */ }
  return getToken();
};

const fetchJson = async (path) => {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${_token()}` },
  });
  if (!res.ok) return null;
  return res.json();
};

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 60000;
  if (diff < 1) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}m ago`;
  if (diff < 60 * 24) return `${Math.floor(diff / 60)}h ago`;
  return d.toLocaleDateString();
};

export const PortalNotificationBell = ({ onClick, className }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data: annData } = useQuery({
    queryKey: ['portal-announcements-count'],
    queryFn: () => fetchJson('/api/school/portal/announcements'),
    refetchInterval: 30000,
  });
  const { data: portalCount } = useQuery({
    queryKey: ['portal-notifications-count'],
    queryFn: () => fetchJson('/api/school/portal/notifications/unread-count'),
    refetchInterval: 30000,
  });
  const { data: items = [], refetch } = useQuery({
    queryKey: ['portal-notifications-list'],
    queryFn: () => fetchJson('/api/school/portal/notifications?limit=10'),
    enabled: open,
  });

  const annCount = annData?.unread_count || 0;
  const notifCount = portalCount?.unread || 0;
  const total = annCount + notifCount;

  const markRead = useMutation({
    mutationFn: (id) => fetch(`${API_URL}/api/school/portal/notifications/${id}/read`, {
      method: 'POST', headers: { Authorization: `Bearer ${_token()}` },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-notifications-count'] });
      refetch();
    },
  });
  const markAll = useMutation({
    mutationFn: () => fetch(`${API_URL}/api/school/portal/notifications/read-all`, {
      method: 'POST', headers: { Authorization: `Bearer ${_token()}` },
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portal-notifications-count'] });
      refetch();
    },
  });

  const toggle = (e) => {
    e?.stopPropagation?.();
    setOpen(o => !o);
    onClick?.();
  };

  return (
    <div className="relative">
      <button onClick={toggle}
              className={cn("relative p-2 rounded-lg hover:bg-white/10 transition-colors", className)}
              data-testid="portal-notification-bell">
        <Bell className="h-5 w-5" />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
                data-testid="notification-badge">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-xl bg-white border z-50 overflow-hidden"
               data-testid="notification-dropdown">
            <div className="flex items-center justify-between p-3 border-b bg-slate-50">
              <span className="text-sm font-semibold text-slate-700">Notifications</span>
              <div className="flex gap-1">
                {notifCount > 0 && (
                  <button onClick={() => markAll.mutate()}
                          className="text-[11px] text-indigo-600 hover:underline"
                          data-testid="notif-mark-all-read">
                    <CheckCheck className="h-3 w-3 inline mr-0.5" /> Mark all
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">
                  You&apos;re all caught up.
                </div>
              ) : (
                items.map(n => (
                  <div key={n.id}
                       className="p-3 border-b last:border-0 hover:bg-slate-50 flex gap-2 items-start"
                       data-testid={`notif-item-${n.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{n.title}</p>
                      <p className="text-xs text-slate-500 line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{fmt(n.created_at)}</p>
                    </div>
                    <button
                      onClick={() => markRead.mutate(n.id)}
                      className="text-slate-400 hover:text-emerald-600 shrink-0"
                      title="Mark read"
                      data-testid={`notif-read-${n.id}`}>
                      <Check className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PortalNotificationBell;
